import React, { PureComponent } from 'react';
import DayPicker from 'react-day-picker';
import { Col } from 'styled-bootstrap-grid';
import moment from 'moment';
import LazyLoad from 'react-lazyload';
import styled from 'styled-components';

// Components
import '../styles/calendar.css';
import { CalendarEventCard } from './calendarEventCard';

// Utils
import { dashedDate, calendarDate } from '../utility/formatDate';
import { formatTitle } from '../utility/formatTitle';

const Calendar = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`;

const CalendarDay = styled.div`
  height: 100%;
  position: relative;
  font-family: 'Mabry-Regular';

  ul {
    margin: 0;
    padding: 0;
    list-style: none;
    // height: 100%;
  }

  li:before {
    content: '○';
    font-weight: 600;
    color: #3c82e6;
    display: inline-block;
    width: 12px;
    margin-right: 3px;
  }

  li {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow-x: hidden;
    margin-bottom: 7px;
    z-index: 1000;
    cursor: pointer;

    &:hover {
      color: #3c82e6;
    }
  }
`;

const CalendarDate = styled.div`
  font-size: 20px;

  @media (max-width: 767px) {
    font-size: 15px;
  }
`;

const ButtonContainer = styled.div`
  & > button {
    -webkit-appearance: none;
    border: 0;
    padding: 0;
    margin: 0;
    background-color: #3c82e6;
    border-radius: 50%;
    width: 50px;
    height: 50px;
    color: #fff;
    line-height: 1;
    position: relative;

    font-size: 28px;

    &:first-child {
      float: left;
    }

    &:last-child {
      float: right;
    }

    & > span {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      height: 28px;
      line-height: 20px;
      cursor: pointer;
    }

    &:hover {
      background-color: #000;
    }
  }

  @media (max-width: 767px) {
    & > button {
      font-size: 21px;
      line-height: 22px;

      width: 30px;
      height: 30px;
    }
  }
`;

const WeekdayTitle = styled.div`
  font-size: 40px;
  padding: 18px;

  flex: 100% 1;
  display: block;
  color: #000;
  text-align: center;

  background-color: #3c82e6;
  border-right: 2px solid #ebfaff;

  &:first-of-type {
    border-left: 2px solid #3c82e6;
  }

  &:last-of-type {
    border-right: 2px solid #3c82e6;
  }
`;

const ActiveCalendarMonth = styled.h2`
  font-size: 80px;
  text-transform: uppercase;

  @media (max-width: 767px) {
    font-size: 50px;
  }
`;

const MultiDayEvent = styled.li`
  color: #639beb;

  &:before {
    color: #639beb !important;
  }

  &:hover {
    color: #3c82e6 !important;
  }
`;

const WeekNumbers = () => {
  let dayPickerWeeks = document.querySelectorAll('.DayPicker-Week');
  dayPickerWeeks.forEach(function(week, index) {
    week.setAttribute('data-week', index);
  });
};

const Navbar = ({ onPreviousClick, onNextClick, className, month }) => {
  return (
    <ButtonContainer className={className}>
      <button onClick={() => onPreviousClick()}>
        <span>←</span>
      </button>

      <ActiveCalendarMonth>{calendarDate(month)}</ActiveCalendarMonth>

      <button onClick={() => onNextClick()}>
        <span>→</span>
      </button>
    </ButtonContainer>
  );
};

const Weekday = ({ weekday, className, localeUtils, locale }) => {
  const weekdayName = localeUtils.formatWeekdayLong(weekday, locale);
  return (
    <WeekdayTitle className={className} title={weekdayName}>
      {weekdayName.slice(0, 1)}
    </WeekdayTitle>
  );
};

let sku;

export default class CalendarView extends PureComponent {
  constructor(props) {
    super(props);
    this.addDayToState = this.addDayToState.bind(this);
    this.clearDay = this.clearDay.bind(this);
    this.setActivePrice = this.setActivePrice.bind(this);

    let currentTime = moment();

    this.state = {
      selectedDay: undefined,
      eventStart: undefined,
      events: props.events,
      displayInfoPanel: false,
      targetParent: null,
      isActive: false,
      weekdayNumber: 0,
      positionLeft: -5,
      positionTop: -5,
      currentTime: currentTime,
      modalIsOpen: false,
    };
    this.renderDay = this.renderDay.bind(this);
    this.updateCalendarDimensions = this.updateCalendarDimensions.bind(this);
  }

  componentDidMount(props) {
    WeekNumbers();
  }

  componentDidUpdate(prevProps) {
    // Listen to window resize events
    window.addEventListener('resize', this.updateCalendarDimensions);

    // Typical usage (don't forget to compare props):
    if (this.props.events !== prevProps.events) {
      this.setState({
        events: this.props.events,
      });
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateCalendarDimensions);
  }

  updateCalendarDimensions() {
    if (this.state.targetParent !== null) {
      let targetParentDetails = this.state.targetParent.getBoundingClientRect();

      if (this.state.weekdayNumber === 0) {
        let calculateLeft = targetParentDetails.width * 2;

        this.setState({
          positionLeft: calculateLeft,
        });
      }

      this.setState({
        infoPanel: targetParentDetails,
      });
    }
  }

  addDayToState(day) {
    this.setState({
      selectedDay: dashedDate(day),
      weekdayNumber: day.getDay(),
    });
  }

  clearDay() {
    this.setState({
      selectedDay: null,
      weekdayNumber: null,
      targetParent: null,
      clickedItem: null,
      modalIsOpen: false,
    });
  }

  showCalendarEvent(e, index) {
    this.setState({
      displayInfoPanel: false,
      clickedItem: null,
      modalIsOpen: false,
      positionLeft: -5,
      positionTop: -5,
    });

    let targetParent = e.currentTarget.closest('.DayPicker-Day');
    let targetParentDetails = targetParent.getBoundingClientRect();
    let targetWeek = parseInt(
      e.currentTarget.closest('.DayPicker-Week').dataset.week
    );
    let calculateTop;

    if (targetWeek === 0) {
      this.setState({
        positionTop: 2,
      });
    } else if (targetWeek === 1) {
      this.setState({
        positionTop: 0,
      });
    } else if (targetWeek === 2) {
      calculateTop = targetParentDetails.height + 4;

      this.setState({
        positionTop: 0,
      });
    } else if (targetWeek === 3) {
      calculateTop = 0;

      this.setState({
        positionTop: calculateTop,
      });
    } else if (targetWeek === 4) {
      calculateTop = -Math.abs(targetParentDetails.height + 4);

      this.setState({
        positionTop: calculateTop,
      });
    }

    let calculateLeft;

    if (this.state.weekdayNumber === 0) {
      calculateLeft = -Math.abs(targetParentDetails.width * 3);

      this.setState({
        positionLeft: calculateLeft,
      });
    } else if (this.state.weekdayNumber === 6) {
      calculateLeft = -Math.abs(targetParentDetails.width * 3);

      this.setState({
        positionLeft: calculateLeft,
      });
    } else if (this.state.weekdayNumber === 5) {
      calculateLeft = -Math.abs(targetParentDetails.width * 3);

      this.setState({
        positionLeft: calculateLeft,
      });
    } else if (this.state.weekdayNumber === 4) {
      calculateLeft = targetParentDetails.width;

      this.setState({
        positionLeft: calculateLeft,
      });
    } else if (this.state.weekdayNumber === 3) {
      calculateLeft = targetParentDetails.width;

      this.setState({
        positionLeft: calculateLeft,
      });
    } else if (this.state.weekdayNumber === 2) {
      calculateLeft = targetParentDetails.width;

      this.setState({
        positionLeft: calculateLeft,
      });
    } else if (this.state.weekdayNumber === 1) {
      calculateLeft = targetParentDetails.width;

      this.setState({
        positionLeft: calculateLeft,
      });
    }

    this.setState({
      targetParent: targetParent,
      infoPanel: targetParentDetails,
      clickedItem: index,
      modalIsOpen: true,
    });
  }

  mouseOut(e) {
    this.setState({
      displayInfoPanel: false,
      clickedItem: null,
      modalIsOpen: false,
    });
  }

  setActivePrice = (price, sku) => {
    this.setState({
      events: this.state.events.map(product => {
        if (product.node.sku === sku) {
          product.node.activePrice = price;
        }
        return product;
      }),
    });
  };

  renderDay(day) {
    const date = day.getDate();
    const momentDate = moment(day);

    let dd = day.getDate();
    let mm = day.getMonth() + 1;
    let yyyy = day.getFullYear();

    if (dd < 10) {
      dd = '0' + dd;
    }

    if (mm < 10) {
      mm = '0' + mm;
    }
    day = yyyy + '-' + mm + '-' + dd;

    //
    let singleDayMatchingEvents = this.state.events
      .filter(
        event =>
          event.node.data.rawDate === day && event.node.data.rawEndDate === null
      )
      .map(product => {
        sku = 'sku_' + product.node.prismicId;
        return (product.node = {
          ...product.node,
          ...product.data,
          // activePrice: 'full_price',
          quantity: 1,
          sku: sku,
          productType: 'event',
        });
      });

    let multiDayMatchingEvents = this.state.events
      .filter(event => event.node.data.rawEndDate !== null)
      .filter(event => {
        let startDate = moment(event.node.data.rawDate);
        let endDate = moment(event.node.data.rawEndDate).add(1, 'days');
        let betweenDates = moment(momentDate).isBetween(startDate, endDate);

        if (betweenDates === true) {
          return event;
        }
      })
      .map(product => {
        sku = 'sku_' + product.node.prismicId;
        return (product.node = {
          ...product.node,
          ...product.data,
          quantity: 1,
          sku: sku,
          productType: 'event',
        });
      });

    return (
      <CalendarDay>
        <CalendarDate>{date}</CalendarDate>
        <ul>
          <LazyLoad>
            {multiDayMatchingEvents.map((product, index) => {
              return (
                <React.Fragment key={index + 100}>
                  <MultiDayEvent
                    onClick={e => this.showCalendarEvent(e, index + 100)}
                  >
                    {formatTitle(product.data.title.text)}
                    {this.state.selectedDay === day &&
                    this.state.clickedItem === index + 100 ? (
                      <CalendarEventCard
                        link={product.uid}
                        details={product.data}
                        positionTop={this.state.positionTop}
                        positionLeft={this.state.positionLeft}
                        infoPanel={this.state.infoPanel}
                        sku={product.sku}
                      />
                    ) : null}
                  </MultiDayEvent>
                </React.Fragment>
              );
            })}

            {singleDayMatchingEvents
              .filter(product => {
                let eventDateTime = moment(
                  product.data.rawDate + ' ' + product.data.start_time
                );

                let pastEventCheck = moment(
                  this.state.currentTime
                ).isSameOrAfter(eventDateTime);

                return pastEventCheck === false;
              })
              .map((product, index) => {
                return (
                  <React.Fragment key={index}>
                    <li onClick={e => this.showCalendarEvent(e, index)}>
                      {formatTitle(product.data.title.text)}
                      {this.state.selectedDay === product.data.rawDate &&
                      this.state.clickedItem === index ? (
                        <CalendarEventCard
                          link={product.uid}
                          details={product.data}
                          positionTop={this.state.positionTop}
                          positionLeft={this.state.positionLeft}
                          infoPanel={this.state.infoPanel}
                          sku={product.sku}
                        />
                      ) : null}
                    </li>
                  </React.Fragment>
                );
              })}
          </LazyLoad>
        </ul>
      </CalendarDay>
    );
  }

  render(props) {
    return (
      <Col col={12}>
        <Calendar>
          <DayPicker
            renderDay={this.renderDay}
            onDayMouseEnter={this.addDayToState}
            onDayMouseLeave={this.clearDay}
            onDayTouchStart={this.addDayToState}
            navbarElement={<Navbar />}
            weekdayElement={<Weekday />}
            firstDayOfWeek={1}
            onMonthChange={WeekNumbers}
          />
        </Calendar>
      </Col>
    );
  }
}
